import { getBorderStyle, getBorderWidth } from '$lib/global/helper/border';
import { getBorderRadius } from '$lib/global/helper/border-radius';
import { getShadow } from '$lib/global/helper/shadow';

import type {
    BrandBorderRadius,
    BrandBorderStyle,
    BrandBorderWidth,
    BrandShadow,
    Theme,
} from '$lib/types/Theme';

export interface ResolvedAccordionItemStyling {
    // Inline colours.
    surfaceColor: string | undefined;
    borderColor: string | undefined;
    // Wrapper class tokens (applied via cn).
    borderRadiusClass: string | undefined;
    shadowClass: string | undefined;
    borderWidthClass: string | undefined;
    borderStyleClass: string | undefined;
}

interface Params {
    theme: Theme | undefined;
    // The per-item box.backgroundColor — takes precedence over theme.surface when non-transparent,
    // matching the React editor's surfaceColor resolution.
    boxBackgroundColor: string | undefined;
}

const isMeaningfulColor = (value: string | undefined): value is string =>
    !!value && value !== 'transparent';

// Maps theme tokens into the flat shape AccordionItem consumes.
// Priority: theme > undefined (legacy).
export const resolveAccordionItemStyling = ({
    theme,
    boxBackgroundColor,
}: Params): ResolvedAccordionItemStyling => {
    const themeShadowClass = theme?.shadow
        ? getShadow('accordionItem', theme.shadow as BrandShadow)
        : undefined;
    const themeBorderWidthClass = theme?.borderWidth
        ? getBorderWidth(theme.borderWidth as BrandBorderWidth)
        : undefined;
    const themeBorderStyleClass = theme?.borderStyle
        ? getBorderStyle(theme.borderStyle as BrandBorderStyle)
        : undefined;
    // Border-radius: if theme.borderRadius is set we apply it at scale C. If it is not set,
    // `getBorderRadius` falls back to the existing themeBorderRadius store — preserving legacy
    // rendering.
    const borderRadiusClass = getBorderRadius(
        'faqItem',
        theme?.borderRadius as BrandBorderRadius | undefined,
    );

    const surfaceColor = isMeaningfulColor(boxBackgroundColor)
        ? boxBackgroundColor
        : theme?.surface;

    return {
        surfaceColor,
        borderColor: theme?.border,
        borderRadiusClass,
        shadowClass: themeShadowClass,
        borderWidthClass: themeBorderWidthClass,
        borderStyleClass: themeBorderStyleClass,
    };
};
